import { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { AuthSessionContext } from '@context/AuthContext'
import { useProductReadContext } from '@context/ProductContext'
import { useArabicSessionLanguage } from '@context/SessionSettingsContext'

import { formatDate } from '@utils/date.utils'
import { getTransactionDisplay } from '@utils/transaction.utils'

import { TransactionDto } from '@model/TransactionDto'
import { CurrencyType } from '@model/WalletDto'

import { useFormatNumber } from '@hooks/useFormatNumber'
import { useGenerateDocumentFile } from '@hooks/useGenerateDocumentFile'
import { useMixedText } from '@hooks/useMixedText'

export const useTransactionHistoryDocuments = (data: TransactionDto[], walletId?: string) => {
  const { t } = useTranslation()
  const [auth] = useContext(AuthSessionContext)
  const dateFormat = auth?.dateFormatType?.name
  const { isDefaultCFDProductType } = useProductReadContext()
  const isArabic = useArabicSessionLanguage()
  const { parseMixedText } = useMixedText()
  const { formatMoney } = useFormatNumber()

  const tableBody = () => {
    const headerCsvData = [
      [
        t('Type'),
        t('TransactionHistory.Request Date'),
        t('From'),
        t('To'),
        t('Wallet.Reference number'),
        t('Status'),
        t('Amount'),
      ],
    ]

    return data.reduce(
      (previousValue, currentValue) => {
        const { fields, amountSign } = getTransactionDisplay(currentValue, 'client', {
          isArabic,
          authId: auth?.id,
          isDefaultCFDProductType: isDefaultCFDProductType(),
          walletId,
        })
        const date = formatDate(currentValue.createdDate, { formatType: dateFormat })

        const formattedAmount = `${amountSign}${formatMoney(
          fields.amount,
          fields.currency as CurrencyType
        )}`

        const row = [
          currentValue.type.name,
          isArabic ? date.split(' ').reverse().join(' ') : date,
          parseMixedText(fields.from),
          parseMixedText(fields.to),
          currentValue.referenceId,
          currentValue.state.name,
          formattedAmount,
        ]

        return previousValue.concat([isArabic ? row.reverse() : row])
      },
      isArabic ? headerCsvData.map((row) => row.reverse()) : headerCsvData
    )
  }

  return useGenerateDocumentFile('transaction_report', tableBody())
}
